import { toBool, toFiniteCount, toFiniteNumber } from '../formatters';

export const getResultTokenCounts = (result = {}) => {
  const usage = result && typeof result.usage === 'object' ? result.usage : {};
  const input = toFiniteCount(
    result?.input_tokens_consumed ??
      result?.prompt_tokens ??
      usage?.prompt_tokens ??
      usage?.input_tokens,
  );
  const output = toFiniteCount(
    result?.output_tokens_consumed ??
      result?.completion_tokens ??
      usage?.completion_tokens ??
      usage?.output_tokens,
  );
  const total = toFiniteCount(
    result?.total_tokens_consumed ?? result?.total_tokens ?? usage?.total_tokens,
  );
  const hasAnyToken = input !== null || output !== null || total !== null;
  if (!hasAnyToken) {
    return { input: null, output: null, total: null };
  }
  if ((input ?? 0) === 0 && (output ?? 0) === 0 && (total ?? 0) === 0) {
    return { input: null, output: null, total: null };
  }
  const resolvedTotal =
    total !== null ? total : input !== null && output !== null ? input + output : null;
  return { input, output, total: resolvedTotal };
};

const normalizeResultSource = (sourceValue, fallbackCacheHit = false, options = {}) => {
  const allowUnknown = options?.allowUnknown === true;
  const rawSource = String(sourceValue || '').trim().toLowerCase();
  if (rawSource === 'cache' || rawSource === 'provider') {
    return rawSource;
  }
  if (allowUnknown && rawSource === 'unknown') {
    return 'unknown';
  }
  return fallbackCacheHit ? 'cache' : 'provider';
};

export const getSourceDisplayLabel = (sourceValue) => {
  if (sourceValue === 'cache') return 'CACHE';
  if (sourceValue === 'provider') return 'LIVE';
  return 'UNKNOWN';
};

const parseOptionalBoolFlag = (value) => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'string' && !value.trim()) return null;
  return toBool(value, false);
};

const REVIEWER_NOTE_SOURCES = new Set(['reviewer', 'human', 'manual', 'user']);
const normalizeNoteCompareText = (value) =>
  String(value || '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase();

export const getTrustedReviewNote = (item = {}) => {
  if (!item || typeof item !== 'object') return '';
  const reviewNote = typeof item.review_note === 'string' ? item.review_note : '';
  const normalizedNote = normalizeNoteCompareText(reviewNote);
  if (!normalizedNote) return '';

  const noteSource = normalizeNoteCompareText(item.review_note_source);
  return REVIEWER_NOTE_SOURCES.has(noteSource) ? reviewNote : '';
};

export const getResultCacheInfo = (result = {}) => {
  const cacheHit = toBool(result?.cache_hit, false);
  const normalizedSource = normalizeResultSource(result?.analysis_source, cacheHit);
  const cacheKey = typeof result?.cache_key === 'string' ? result.cache_key.trim() : '';
  return {
    cacheHit: normalizedSource === 'cache' || cacheHit,
    source: normalizedSource,
    cacheKey,
  };
};

export const getResultAttemptSourceInfo = (result = {}) => {
  const cacheInfo = getResultCacheInfo(result);
  const explicitFinalCacheHit = parseOptionalBoolFlag(result?.final_attempt_cache_hit);
  const explicitFirstCacheHit = parseOptionalBoolFlag(result?.first_attempt_cache_hit);
  const finalCacheHit = explicitFinalCacheHit !== null ? explicitFinalCacheHit : cacheInfo.cacheHit;
  const firstCacheHit = explicitFirstCacheHit !== null ? explicitFirstCacheHit : finalCacheHit;
  const explicitFinalSource =
    typeof result?.final_attempt_source === 'string' ? result.final_attempt_source.trim() : '';
  const explicitFirstSource =
    typeof result?.first_attempt_source === 'string' ? result.first_attempt_source.trim() : '';
  const finalSource = explicitFinalSource
    ? normalizeResultSource(explicitFinalSource, finalCacheHit, { allowUnknown: true })
    : normalizeResultSource(result?.analysis_source, finalCacheHit, { allowUnknown: true });
  const firstSource = explicitFirstSource
    ? normalizeResultSource(explicitFirstSource, firstCacheHit, { allowUnknown: true })
    : finalSource;
  return {
    firstSource,
    finalSource,
    firstCacheHit,
    finalCacheHit,
    cacheKey: cacheInfo.cacheKey,
  };
};

export const getRunCacheStats = (run = {}) => {
  const runResults = Array.isArray(run?.results) ? run.results : [];
  const explicitCacheHitCount = toFiniteCount(run?.cache_hit_count);
  const computedCacheHitCount = runResults.reduce((sum, resultEntry) => {
    const cacheInfo = getResultCacheInfo(resultEntry);
    return cacheInfo.cacheHit ? sum + 1 : sum;
  }, 0);
  const count = explicitCacheHitCount !== null ? explicitCacheHitCount : computedCacheHitCount;
  const total = runResults.length;
  const explicitRate = toFiniteNumber(run?.cache_hit_rate);
  const rate =
    explicitRate !== null
      ? Math.max(0, Math.min(1, explicitRate))
      : total > 0
        ? count / total
        : 0;
  return { count, total, rate };
};

export const getRunTokenTotals = (run = {}) => {
  const runInput = toFiniteCount(run?.input_tokens_consumed);
  const runOutput = toFiniteCount(run?.output_tokens_consumed);
  const runTotal = toFiniteCount(run?.total_tokens_consumed);
  const hasRunTokenField = runInput !== null || runOutput !== null || runTotal !== null;
  const hasRunTokenValue = (runInput ?? 0) > 0 || (runOutput ?? 0) > 0 || (runTotal ?? 0) > 0;
  if (hasRunTokenField && hasRunTokenValue) {
    const total =
      runTotal !== null ? runTotal : runInput !== null && runOutput !== null ? runInput + runOutput : null;
    return { input: runInput, output: runOutput, total };
  }

  const runResults = Array.isArray(run?.results) ? run.results : [];
  let hasInput = false;
  let hasOutput = false;
  let hasTotal = false;
  let input = 0;
  let output = 0;
  let total = 0;
  runResults.forEach((result) => {
    const counts = getResultTokenCounts(result);
    if (counts.input !== null) {
      hasInput = true;
      input += counts.input;
    }
    if (counts.output !== null) {
      hasOutput = true;
      output += counts.output;
    }
    if (counts.total !== null) {
      hasTotal = true;
      total += counts.total;
    }
  });
  if (!hasInput && !hasOutput && !hasTotal) {
    return { input: null, output: null, total: null };
  }
  if (input <= 0 && output <= 0 && total <= 0) {
    return { input: null, output: null, total: null };
  }
  if (!hasTotal && hasInput && hasOutput) {
    total = input + output;
    hasTotal = true;
  }
  return {
    input: hasInput ? input : null,
    output: hasOutput ? output : null,
    total: hasTotal ? total : null,
  };
};

export const getRunProgressMetrics = (run = {}) => {
  const rawTotal = toFiniteCount(run?.total_data_points ?? run?.data_points);
  const total = rawTotal !== null && rawTotal > 0 ? rawTotal : null;
  let successful = toFiniteCount(run?.successful_data_points);
  let skipped = toFiniteCount(run?.skipped_data_points);
  let attempted = toFiniteCount(run?.attempted_data_points);
  if (successful === null) successful = 0;
  if (skipped === null) skipped = 0;
  if (attempted === null) attempted = successful + skipped;
  if (total !== null) attempted = Math.min(total, attempted);

  const explicitPercent = toFiniteNumber(run?.progress_percent);
  const progressPercent =
    explicitPercent !== null
      ? Math.max(0, Math.min(100, explicitPercent))
      : total && total > 0
        ? Math.max(0, Math.min(100, (attempted / total) * 100))
        : 0;
  const avgDurationSeconds = toFiniteNumber(run?.avg_duration_seconds);
  const etaSeconds = toFiniteNumber(run?.eta_seconds);
  return {
    total,
    attempted,
    successful,
    skipped,
    progressPercent,
    avgDurationSeconds,
    etaSeconds,
    currentLot: run?.current_lot,
    currentIndex: toFiniteCount(run?.current_index),
    currentAttempt: toFiniteCount(run?.current_attempt),
    phase: run?.phase || 'running',
    lastError: run?.last_error || '',
    updatedAt: run?.updated_at || null,
    datasetTruncated: toBool(run?.dataset_truncated, false),
  };
};

export const getRunPointStats = (run = {}) => {
  const attempted = toFiniteCount(
    run?.attempted_data_points ?? run?.data_points ?? run?.iterations ?? run?.num_iterations,
  );
  const successful = toFiniteCount(
    run?.successful_data_points ?? (Array.isArray(run?.results) ? run.results.length : null),
  );
  const explicitSkipped = toFiniteCount(run?.skipped_data_points);
  const inferredSkipped =
    attempted !== null && successful !== null ? Math.max(0, attempted - successful) : null;
  const skipped = explicitSkipped !== null ? explicitSkipped : inferredSkipped !== null ? inferredSkipped : 0;
  return { attempted, successful, skipped };
};

export const formatRunDataPoints = (run = {}) => {
  const { attempted, successful, skipped } = getRunPointStats(run);
  const truncSuffix = run?.dataset_truncated ? ' (truncated)' : '';
  if (attempted !== null && successful !== null) {
    return skipped > 0 ? `${successful}/${attempted}${truncSuffix}` : `${attempted}${truncSuffix}`;
  }
  if (attempted !== null) return `${attempted}${truncSuffix}`;
  if (successful !== null) return `${successful}${truncSuffix}`;
  return '-';
};

export const getRunDataPointsHint = (run = {}) => {
  const { attempted, successful, skipped } = getRunPointStats(run);
  if (attempted !== null && successful !== null) {
    return skipped > 0
      ? `Successful ${successful} of ${attempted} (${skipped} skipped)`
      : `Successful ${successful} of ${attempted}`;
  }
  if (attempted !== null) return `Data points: ${attempted}`;
  if (successful !== null) return `Data points: ${successful}`;
  return '';
};
